import { useState } from "react";
import { useQuery, useQueryClient, type QueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import type { CategoryChange, MailCategory } from "@shared/library";
import { isApiError } from "@web/lib/api";
import { registerUndo } from "@web/lib/undoRegistry";
import { Button } from "@web/ui/Button";
import { categoriesKey, libraryApi, libraryKey } from "./api";

export function useCategories() {
  return useQuery({ queryKey: categoriesKey, queryFn: libraryApi.categories });
}
export function categoryError(error: unknown, fallback = "Couldn’t change categories just now.") {
  return isApiError(error) && error.serverMessage ? error.serverMessage : fallback;
}
function acceptChange(client: QueryClient, result: CategoryChange, message: string) {
  client.setQueryData(categoriesKey, result.categories);
  void client.invalidateQueries({ queryKey: libraryKey });
  registerUndo({
    message,
    undo: async () => {
      const out = await libraryApi.undoCategory(result.undoId);
      client.setQueryData(categoriesKey, out.categories);
      void client.invalidateQueries({ queryKey: libraryKey });
    },
    doneMessage: () => "Category change undone.",
  });
}
export function CreateCategory({
  onCreated,
  onBusy,
  disabled = false,
}: {
  disabled?: boolean;
  onCreated?: (category: MailCategory) => void;
  onBusy?: (busy: boolean) => void;
}) {
  const client = useQueryClient();
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  async function create() {
    if (busy || disabled || !name.trim()) return;
    setBusy(true);
    onBusy?.(true);
    setError("");
    try {
      const result = await libraryApi.createCategory(name);
      acceptChange(client, result, "Category added.");
      onCreated?.(result.category);
      setName("");
    } catch (e) {
      setError(categoryError(e));
    } finally {
      setBusy(false);
      onBusy?.(false);
    }
  }
  return (
    <form
      aria-label="New category"
      onSubmit={(e) => {
        e.preventDefault();
        void create();
      }}
      className="flex flex-col gap-2"
    >
      <div className="flex flex-wrap gap-2">
        <input
          aria-label="New category name"
          placeholder="New category name"
          maxLength={60}
          className="min-w-36 flex-1 rounded-lg border border-line bg-bg px-3 py-2 text-ink"
          value={name}
          onChange={(e) => setName(e.target.value)}
          disabled={busy || disabled}
        />
        <Button type="submit" size="sm" disabled={disabled || !name.trim()} busy={busy}>
          Add category
        </Button>
      </div>
      {error ? (
        <p role="alert" className="text-sm text-note-ink">
          {error}
        </p>
      ) : null}
    </form>
  );
}
function CategoryRow({ category }: { category: MailCategory }) {
  const client = useQueryClient();
  const [name, setName] = useState(category.name);
  const [busy, setBusy] = useState(false);
  async function change(remove: boolean) {
    if (busy) return;
    setBusy(true);
    try {
      const result = remove
        ? await libraryApi.removeCategory(category.id)
        : await libraryApi.renameCategory(category.id, name);
      acceptChange(
        client,
        result,
        remove ? "Category removed. Emails remain in All saved." : "Category renamed.",
      );
    } catch (e) {
      toast(categoryError(e));
    } finally {
      setBusy(false);
    }
  }
  return (
    <li className="flex flex-wrap items-center gap-2">
      <input
        aria-label={`Name for ${category.name}`}
        maxLength={60}
        value={name}
        className="min-w-40 flex-1 rounded-lg border border-line bg-bg px-3 py-2 text-ink"
        onChange={(e) => setName(e.target.value)}
        disabled={busy}
      />
      <Button
        size="sm"
        busy={busy}
        disabled={!name.trim() || name === category.name}
        onClick={() => void change(false)}
      >
        Rename
      </Button>
      <Button
        size="sm"
        variant="ghost"
        disabled={busy}
        aria-label={`Remove category ${category.name}`}
        onClick={() => void change(true)}
      >
        Remove
      </Button>
    </li>
  );
}
export function CategoryManager() {
  const [open, setOpen] = useState(false);
  const categories = useCategories();
  return (
    <section aria-label="Manage categories" className="rounded-xl border border-line bg-paper p-4">
      <Button size="sm" aria-expanded={open} onClick={() => setOpen((v) => !v)}>
        Manage categories
      </Button>
      {open ? (
        <div className="mt-3 flex flex-col gap-3">
          <p className="text-sm text-muted">
            Rename or remove any category. Removing a category keeps its emails in All saved and
            stops its filing suggestions. Undo restores it.
          </p>
          {categories.isError ? (
            <Button onClick={() => void categories.refetch()}>Try again</Button>
          ) : null}
          <ul className="flex flex-col gap-2">
            {categories.data?.map((c) => (
              <CategoryRow key={`${c.id}:${c.name}`} category={c} />
            ))}
          </ul>
          <CreateCategory />
        </div>
      ) : null}
    </section>
  );
}
